import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, Check, CheckCheck, X, Gift, AlertCircle, Info, Trophy, Package } from "lucide-react";
import { toast } from "sonner";

// Icon + color per notification type
const TYPE_META = {
  info:    { icon: Info,        color: "#2563EB", bg: "#EFF6FF" },
  success: { icon: Check,       color: "#16A34A", bg: "#F0FDF4" },
  reward:  { icon: Gift,        color: "#D97706", bg: "#FFFBEB" },
  warning: { icon: AlertCircle, color: "#EA580C", bg: "#FFF7ED" },
  error:   { icon: X,           color: "#DC2626", bg: "#FEF2F2" },
  order:   { icon: Package,     color: "#7C3AED", bg: "#F5F3FF" },
  rank:    { icon: Trophy,      color: "#CA8A04", bg: "#FEFCE8" },
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const s = Math.floor((now - d) / 1000);
  if (s < 30) return "à l'instant";
  if (s < 60) return `il y a ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h}h`;
  const j = Math.floor(h / 24);
  if (j < 7) return `il y a ${j}j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
};

// role: "admin" | "affiliate" | "client"
// apiCall: (method, url, data?) wrapper
export const NotificationCenter = ({ apiCall, role = "affiliate" }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const popupRef = useRef(null);
  const btnRef = useRef(null);

  const isClient = role === "client";
  const isAdmin = role === "admin";
  const isAffiliate = role === "affiliate";
  // For affiliate: ALSO fetch the new /affiliate/notifications (subscription expiry, etc.)
  // For admin: use /admin/notifications endpoint
  let listPath, countPath, markPath, markAllPath;
  if (isClient) {
    listPath = "/client/notifications";
    countPath = "/client/notifications/unread-count";
    markPath = (id) => `/client/notifications/mark-read/${id}`;
    markAllPath = "/client/notifications/mark-all-read";
  } else if (isAdmin) {
    listPath = "/admin/notifications";
    countPath = "/admin/notifications?only_unread=true&limit=1";
    markPath = (id) => `/admin/notifications/${id}/mark-read`;
    markAllPath = "/admin/notifications/mark-all-read";
  } else if (isAffiliate) {
    // Affiliate: keep the legacy /notifications endpoint AS-IS for rank/commission events.
    // Subscription-expired alerts are merged client-side via /affiliate/notifications.
    listPath = "/notifications";
    countPath = "/notifications/unread-count";
    markPath = (id) => `/notifications/mark-read/${id}`;
    markAllPath = "/notifications/mark-all-read";
  } else {
    listPath = "/notifications";
    countPath = "/notifications/unread-count";
    markPath = (id) => `/notifications/mark-read/${id}`;
    markAllPath = "/notifications/mark-all-read";
  }

  const loadCount = useCallback(async () => {
    try {
      if (isAdmin) {
        const r = await apiCall("get", "/admin/notifications?only_unread=true&limit=1");
        setUnread(r.data?.unread_count || 0);
      } else if (isAffiliate) {
        // Sum legacy + new affiliate notification counts
        let total = 0;
        try { const r = await apiCall("get", "/notifications/unread-count"); total += (r.data?.count || 0); } catch { /* ignore */ }
        try { const r2 = await apiCall("get", "/affiliate/notifications?only_unread=true&limit=1"); total += (r2.data?.unread_count || 0); } catch { /* ignore */ }
        setUnread(total);
      } else {
        const r = await apiCall("get", countPath);
        setUnread(r.data?.count || 0);
      }
    } catch { /* ignore */ }
  }, [apiCall, countPath, isAdmin, isAffiliate]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      let arr = [];
      if (isAdmin) {
        const r = await apiCall("get", "/admin/notifications?limit=50");
        arr = r.data?.notifications || [];
      } else if (isAffiliate) {
        // Merge legacy /notifications + /affiliate/notifications (subscription expiry, etc.)
        try {
          const r = await apiCall("get", "/notifications");
          const a = Array.isArray(r.data) ? r.data : (r.data?.notifications || []);
          arr = arr.concat(a);
        } catch { /* ignore */ }
        try {
          const r2 = await apiCall("get", "/affiliate/notifications?limit=50");
          const a2 = (r2.data?.notifications || []).map(n => ({ ...n, _aff: true }));
          arr = arr.concat(a2);
        } catch { /* ignore */ }
        arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      } else {
        const r = await apiCall("get", listPath);
        arr = Array.isArray(r.data) ? r.data : (r.data?.notifications || []);
      }
      setItems(arr);
      setUnread(arr.filter((n) => !n.read).length);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, listPath, isAdmin, isAffiliate]);

  // Poll unread count every 30s
  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  // Close popup on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const markOne = async (n) => {
    if (n.read) return;
    setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
    setUnread((c) => Math.max(0, c - 1));
    try {
      if (isAffiliate && n._aff) {
        await apiCall("post", `/affiliate/notifications/${n.id}/mark-read`);
      } else {
        await apiCall("post", markPath(n.id));
      }
    } catch { /* ignore */ }
  };

  const markAll = async () => {
    if (unread === 0) return;
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try {
      if (isAffiliate) {
        // Mark BOTH sources read
        try { await apiCall("post", "/notifications/mark-all-read"); } catch { /* ignore */ }
        try { await apiCall("post", "/affiliate/notifications/mark-all-read"); } catch { /* ignore */ }
      } else {
        await apiCall("post", markAllPath);
      }
      toast.success("Toutes marquées comme lues");
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={btnRef}
        onClick={toggle}
        className="notif-center-btn"
        data-testid="notif-center-btn"
        aria-label={`Notifications (${unread} non lues)`}
        style={{
          position: "relative",
          background: "transparent",
          border: "1px solid #E2E8F0",
          borderRadius: "10px",
          width: 38, height: 38,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#0F172A",
          transition: "background 0.12s",
        }}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notif-center-badge"
            style={{
              position: "absolute", top: -4, right: -4,
              minWidth: 18, height: 18, padding: "0 4px",
              background: "#DC2626", color: "white",
              borderRadius: 999, fontSize: "0.65rem", fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "2px solid white",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popupRef}
          className="notif-center-panel"
          data-testid="notif-center-panel"
          style={{
            position: "fixed",
            right: "8px",
            left: "8px",
            top: "62px",
            margin: "0 auto",
            maxWidth: "420px",
            width: "auto",
            maxHeight: "min(560px, calc(100vh - 80px))",
            background: "white",
            border: "1px solid #E2E8F0",
            borderRadius: "14px",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
            zIndex: 300,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            animation: "notifc-open 0.18s ease-out",
          }}
        >
          <div style={{
            padding: "0.75rem 0.85rem",
            borderBottom: "1px solid #E2E8F0",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: "0.4rem",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 800 }}>
              <Bell size={16} color="#2563EB" /> Notifications
              {unread > 0 && (
                <span style={{
                  background: "#DBEAFE", color: "#1E40AF",
                  padding: "0.1rem 0.4rem", borderRadius: 999,
                  fontSize: "0.68rem", fontWeight: 700,
                }}>{unread} non lue{unread > 1 ? "s" : ""}</span>
              )}
            </div>
            <div style={{ display: "flex", gap: "0.3rem" }}>
              <button
                className="btn btn-sm"
                onClick={markAll}
                disabled={unread === 0}
                data-testid="notif-center-mark-all"
                style={{
                  background: unread === 0 ? "#F1F5F9" : "#E0E7FF",
                  color: unread === 0 ? "#94A3B8" : "#3730A3",
                  border: "none", padding: "0.3rem 0.55rem",
                  borderRadius: "6px", fontSize: "0.7rem", fontWeight: 700,
                  cursor: unread === 0 ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: "0.2rem",
                }}
              >
                <CheckCheck size={12} /> Tout lire
              </button>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: "#F1F5F9", border: "none",
                  borderRadius: "6px", padding: "0.3rem", cursor: "pointer", color: "#475569",
                }}
                aria-label="Fermer"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && items.length === 0 ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "#64748B", fontSize: "0.8rem" }}>
                Chargement...
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: "2rem 1rem", textAlign: "center", color: "#64748B" }}>
                <Bell size={32} color="#CBD5E1" style={{ margin: "0 auto 0.5rem" }} />
                <div style={{ fontSize: "0.82rem" }}>Aucune notification pour le moment</div>
              </div>
            ) : (
              items.map((n) => {
                const meta = TYPE_META[n.type] || TYPE_META.info;
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => markOne(n)}
                    data-testid={`notif-item-${n.id}`}
                    className="notif-item"
                    style={{
                      width: "100%",
                      display: "flex",
                      gap: "0.6rem",
                      padding: "0.85rem 0.9rem",
                      border: "none",
                      background: n.read ? "white" : "linear-gradient(90deg, #EFF6FF 0%, white 80%)",
                      borderBottom: "1px solid #F1F5F9",
                      textAlign: "left",
                      cursor: "pointer",
                      transition: "background 0.12s",
                    }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: "10px",
                      background: meta.bg, color: meta.color,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Icon size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.3rem", marginBottom: 3 }}>
                        <div style={{
                          fontWeight: n.read ? 600 : 800,
                          fontSize: "0.9rem",
                          color: "#0F172A",
                          flex: 1,
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                        }}>
                          {n.title}
                        </div>
                        {!n.read && (
                          <span style={{
                            width: 8, height: 8, borderRadius: 999, background: "#2563EB",
                            flexShrink: 0, marginTop: 5,
                          }} />
                        )}
                      </div>
                      <div style={{
                        fontSize: "0.8rem",
                        color: "#334155",
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                        whiteSpace: "pre-line",
                      }}>
                        {n.message}
                      </div>
                      {/* Subscription expired: highlight the streaming email + copy button */}
                      {n.category === "subscription_expired" && n.streaming_email && (
                        <div
                          data-testid={`notif-streaming-email-${n.id}`}
                          style={{
                            marginTop: 6,
                            padding: "0.4rem 0.55rem",
                            background: "#FEF2F2",
                            border: "1px solid #FCA5A5",
                            borderLeft: "3px solid #DC2626",
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 6,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: "0.62rem", fontWeight: 700, color: "#B91C1C", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Email à déconnecter
                            </div>
                            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0F172A", wordBreak: "break-all", fontFamily: "monospace" }}>
                              {n.streaming_email}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              try {
                                navigator.clipboard.writeText(n.streaming_email);
                                toast.success("Email copié");
                              } catch {
                                toast.error("Erreur copie");
                              }
                            }}
                            style={{
                              background: "#DC2626", color: "white", border: "none",
                              borderRadius: 6, padding: "0.2rem 0.5rem",
                              fontSize: "0.7rem", fontWeight: 700, cursor: "pointer",
                              flexShrink: 0,
                            }}
                          >
                            Copier
                          </button>
                        </div>
                      )}
                      <div style={{ fontSize: "0.7rem", color: "#94A3B8", marginTop: 5 }}>
                        {fmtRelative(n.created_at)}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes notifc-open {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .notif-center-btn:hover { background: #F1F5F9 !important; }
        .notif-item:hover { background: #F8FAFC !important; }

        /* Mobile: anchor full-width, allow long text to wrap, larger touch targets */
        @media (max-width: 640px) {
          .notif-center-panel {
            left: 8px !important;
            right: 8px !important;
            top: 56px !important;
            max-height: calc(100vh - 70px) !important;
            border-radius: 12px !important;
          }
          .notif-item {
            padding: 0.95rem 0.9rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationCenter;
